import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Loader2, Check, Plus, Trash2, Copy, GripVertical, DoorOpen } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable row component
const SortableRow = ({ id, children, className }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={className}>
      <TableCell className="w-[40px] p-2">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded touch-none"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>
      {children}
    </TableRow>
  );
};

const EditableAncillaryRoomsTab = ({ event, onUpdate }) => {
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: null, item: null });
  const isInitialMount = useRef(true);
  const pendingChanges = useRef({});
  const saveTimeoutRef = useRef(null);

  // Only sync with parent on initial mount
  useEffect(() => {
    if (isInitialMount.current) {
      // Ensure all items have IDs for drag-and-drop, filter out null items
      const ensureIds = (items) => (items || [])
        .filter(item => item != null)
        .map((item, idx) => ({
          ...item,
          id: item.id || `room-${Date.now()}-${idx}`
        }));
      setRooms(ensureIds(event?.ancillary_rooms));
      isInitialMount.current = false;
    }
  }, [event?.ancillary_rooms]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Renumber items after reorder
  const renumberItems = (items) => {
    return items.map((item, index) => ({
      ...item,
      order: index + 1
    }));
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const validRooms = rooms.filter(item => item?.id);
      const oldIndex = validRooms.findIndex(item => item.id === active.id);
      const newIndex = validRooms.findIndex(item => item.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = renumberItems(arrayMove(validRooms, oldIndex, newIndex));
        setRooms(reordered);
        queueChange({ ancillary_rooms: reordered });
        saveNow();
      }
    }
  };

  // Perform the actual save
  const doSave = useCallback(async () => {
    if (Object.keys(pendingChanges.current).length === 0) return;
    
    setSaving(true);
    const changesToSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    
    try {
      await onUpdate(changesToSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save ancillary rooms:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  // Queue a change with debounce
  const queueChange = useCallback((changes) => {
    pendingChanges.current = { ...pendingChanges.current, ...changes };
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(doSave, 1000);
  }, [doSave]);

  // Save immediately (for blur events)
  const saveNow = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  // Add new room
  const addRoom = () => {
    const newRoom = {
      id: `room-${Date.now()}`,
      order: rooms.length + 1,
      space: '',
      description: ''
    };
    const updated = [...rooms, newRoom];
    setRooms(updated);
    queueChange({ ancillary_rooms: updated });
  };

  // Update room field
  const updateRoom = (index, field, value) => {
    const updated = [...rooms];
    updated[index] = { ...updated[index], [field]: value };
    setRooms(updated);
    queueChange({ ancillary_rooms: updated });
  };

  // Duplicate room
  const duplicateRoom = (index) => {
    const roomToDuplicate = rooms[index];
    const newRoom = {
      ...roomToDuplicate,
      id: `room-${Date.now()}`,
      order: rooms.length + 1,
      space: `${roomToDuplicate.space} (Copy)`
    };
    const updated = renumberItems([...rooms, newRoom]);
    setRooms(updated);
    queueChange({ ancillary_rooms: updated });
    saveNow();
    toast.success('Room duplicated');
  };

  // Delete room (with confirmation)
  const confirmDelete = (index) => {
    setDeleteConfirm({
      open: true,
      index,
      item: rooms[index]
    });
  };

  const executeDelete = () => {
    if (deleteConfirm.index !== null) {
      const updated = renumberItems(rooms.filter((_, i) => i !== deleteConfirm.index));
      setRooms(updated);
      queueChange({ ancillary_rooms: updated });
      saveNow();
      toast.success('Room deleted');
    }
    setDeleteConfirm({ open: false, index: null, item: null });
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <DoorOpen className="h-5 w-5 text-cyan-400" />
            Ancillary Rooms
          </CardTitle>
          <div className="flex items-center gap-2">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Add additional spaces and rooms with their technical requirements.
            </p>
            <Button onClick={addRoom} size="sm" data-testid="add-room-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Room
            </Button>
          </div>

          {rooms.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DoorOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No ancillary rooms added yet.</p>
              <p className="text-sm">Click "Add Room" to add spaces with their descriptions.</p>
            </div>
          ) : (
            <div className="border border-border/50 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="w-[40px]"></TableHead>
                    <TableHead className="w-[250px]">Space</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <TableBody>
                    <SortableContext items={rooms.filter(r => r?.id).map(r => r.id)} strategy={verticalListSortingStrategy}>
                      {rooms.filter(r => r?.id).map((room, index) => (
                        <SortableRow key={room.id} id={room.id} className="border-border/50">
                          <TableCell>
                            <Input
                              value={room.space || ''}
                              onChange={(e) => updateRoom(index, 'space', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Room name..."
                              className="bg-background/50 h-9"
                            />
                          </TableCell>
                          <TableCell>
                            <Textarea
                              value={room.description || ''}
                              onChange={(e) => {
                                updateRoom(index, 'description', e.target.value);
                                // Auto-resize
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                              onBlur={saveNow}
                              placeholder="Technical requirements, notes..."
                              rows={1}
                              className="bg-background/50 resize-none overflow-hidden min-h-[38px]"
                              style={{ height: 'auto' }}
                              ref={(el) => {
                                // Auto-resize on initial render
                                if (el && room.description) {
                                  el.style.height = 'auto';
                                  el.style.height = el.scrollHeight + 'px';
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => duplicateRoom(index)}
                                className="h-8 w-8"
                                title="Duplicate"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => confirmDelete(index)}
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </SortableRow>
                      ))}
                    </SortableContext>
                  </TableBody>
                </DndContext>
              </Table>
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, index: null, item: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Room</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{deleteConfirm.item?.space || 'this room'}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={executeDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default EditableAncillaryRoomsTab;
